'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export type ProductImageGalleryItem = {
  id: string;
  url: string;
  alt: string;
};

type ProductImageLightboxProps = {
  items: ProductImageGalleryItem[];
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIndexChange: (index: number) => void;
  prevLabel: string;
  nextLabel: string;
  counterLabel: (current: number, total: number) => string;
};

const clampIndex = (index: number, total: number) => {
  if (total <= 0) return 0;
  return (index + total) % total;
};

export function ProductImageLightbox({
  items,
  index,
  open,
  onOpenChange,
  onIndexChange,
  prevLabel,
  nextLabel,
  counterLabel,
}: ProductImageLightboxProps) {
  const safeIndex = clampIndex(index, items.length);
  const item = items[safeIndex];
  const hasMany = items.length > 1;

  const goPrev = React.useCallback(
    () => onIndexChange(clampIndex(safeIndex - 1, items.length)),
    [items.length, onIndexChange, safeIndex],
  );

  const goNext = React.useCallback(
    () => onIndexChange(clampIndex(safeIndex + 1, items.length)),
    [items.length, onIndexChange, safeIndex],
  );

  React.useEffect(() => {
    if (!open || !hasMany) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, hasMany, goPrev, goNext]);

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-4 sm:max-w-3xl">
        <div className="flex items-start justify-between gap-3 pr-8">
          <DialogTitle className="text-base font-semibold leading-snug">{item.alt}</DialogTitle>
          {hasMany ? (
            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
              {counterLabel(safeIndex + 1, items.length)}
            </span>
          ) : null}
        </div>

        <div className="relative flex items-center justify-center gap-2">
          {hasMany ? (
            <NavButton label={prevLabel} onClick={goPrev} className="left-0 sm:absolute sm:-left-12">
              <ChevronLeft className="size-5" />
            </NavButton>
          ) : null}

          <div className="bg-muted/30 w-full overflow-hidden rounded-lg border">
            <img
              src={item.url}
              alt={item.alt}
              className="mx-auto block max-h-[70vh] w-full object-contain"
              draggable={false}
            />
          </div>

          {hasMany ? (
            <NavButton label={nextLabel} onClick={goNext} className="right-0 sm:absolute sm:-right-12">
              <ChevronRight className="size-5" />
            </NavButton>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NavButton = ({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <Button
    type="button"
    variant="outline"
    size="icon"
    aria-label={label}
    onClick={onClick}
    className={cn('size-9 shrink-0 rounded-full', className)}
  >
    {children}
  </Button>
);
